import React, { useState, useEffect, useRef, useCallback } from 'react';
import { graphqlQuery, graphqlMutation, graphqlSubscribe } from '../utils/graphqlApi';
import { getSessionTimeline } from '../graphql/queries';
import { postApproval } from '../graphql/mutations';
import { onSessionEntry } from '../graphql/subscriptions';

const STEP_RESULT_ENTRY = 'STEP_RESULT';
const APPROVAL_RESPONSE_ENTRY = 'APPROVAL_RESPONSE';
const MAX_CONSECUTIVE_DENIALS = 2;
const STEP_ADVANCE_DELAY_MS = 1500;
const POLL_INTERVAL_MS = 5000; // Fallback polling interval when subscription misses events
const POLL_MAX_ATTEMPTS = 24; // Max poll attempts (~2 minutes total)

// Safely parse a JSON string or return the value as-is.
const safeParse = (raw) => {
  if (typeof raw !== 'string') return raw;
  try { return JSON.parse(raw); } catch { return raw; }
};

// Determines whether a parsed step result represents a failure.
const isStepFailure = (result) =>
  result &&
  typeof result === 'object' &&
  (result.status === 'FAILED' || result.status === 'ERROR' || result.error || result.failed);

/**
 * StepExecution — displays plan steps one-by-one after plan approval (ALLOW).
 * Each step has approve (✓) and deny (✗) buttons.
 * If the user denies two consecutive steps, the agent is stopped.
 *
 * Props:
 *   sessionId  — the active session identifier
 *   steps      — array of step objects from the structured plan
 */
function StepExecution({ sessionId, steps }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepStatuses, setStepStatuses] = useState({}); // { index: 'APPROVED' | 'DENIED' | 'EXECUTING' | 'COMPLETED' | 'FAILED' }
  const [consecutiveDenials, setConsecutiveDenials] = useState(0);
  const [agentStopped, setAgentStopped] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [stepResults, setStepResults] = useState({}); // { index: resultData }
  const [restored, setRestored] = useState(false);
  const [subscriptionReady, setSubscriptionReady] = useState(false);

  const subscriptionRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollAttemptRef = useRef(0);

  // --- Restore state from DB on mount (handles tab switch / remount) ---
  useEffect(() => {
    if (!sessionId || restored) return;

    (async () => {
      console.log('[StepExecution] Restoring state from DB for session:', sessionId);
      try {
        const [approvalRes, stepResultRes] = await Promise.all([
          graphqlQuery(getSessionTimeline, { sessionId, entryType: APPROVAL_RESPONSE_ENTRY }),
          graphqlQuery(getSessionTimeline, { sessionId, entryType: STEP_RESULT_ENTRY }),
        ]);

        const approvalItems = (approvalRes?.getSessionTimeline?.items || [])
          .filter((e) => e?.entryType === APPROVAL_RESPONSE_ENTRY)
          .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

        const stepResultItems = (stepResultRes?.getSessionTimeline?.items || [])
          .filter((e) => e?.entryType === STEP_RESULT_ENTRY)
          .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

        console.log('[StepExecution] Restore data:', {
          approvalCount: approvalItems.length,
          stepResultCount: stepResultItems.length,
        });

        // Skip the first APPROVAL_RESPONSE (that's the plan approval).
        // Subsequent ones are per-step approvals.
        const stepApprovals = approvalItems.slice(1);

        const restoredStatuses = {};
        const restoredResults = {};
        let lastStepIndex = 0;
        let consecutive = 0;

        stepApprovals.forEach((entry, i) => {
          const parsed = safeParse(entry.data);
          const decision = parsed?.decision;

          if (decision === 'ALLOW') {
            const matchingResult = stepResultItems[i] || null;
            if (matchingResult) {
              const resultData = safeParse(matchingResult.data);
              restoredStatuses[i] = isStepFailure(resultData) ? 'FAILED' : 'COMPLETED';
              restoredResults[i] = resultData;
            } else {
              restoredStatuses[i] = 'EXECUTING';
            }
            consecutive = 0;
          } else if (decision === 'DISCARD') {
            restoredStatuses[i] = 'DENIED';
            consecutive += 1;
          }
          lastStepIndex = i;
        });

        if (Object.keys(restoredStatuses).length > 0) {
          console.log('[StepExecution] Restored step statuses:', restoredStatuses);
          setStepStatuses(restoredStatuses);
          setStepResults(restoredResults);
          setConsecutiveDenials(consecutive);
          if (consecutive >= MAX_CONSECUTIVE_DENIALS) {
            setAgentStopped(true);
          }
          // Advance to next pending step
          const nextIndex = lastStepIndex + (restoredStatuses[lastStepIndex] === 'EXECUTING' ? 0 : 1);
          setCurrentStepIndex(Math.min(nextIndex, steps.length - 1));
        }
      } catch (err) {
        console.warn('[StepExecution] Failed to restore state:', err);
      } finally {
        setRestored(true);
      }
    })();
  }, [sessionId, steps.length, restored]);

  // Subscribe to session timeline for STEP_RESULT entries
  useEffect(() => {
    if (!sessionId) return undefined;

    console.log('[StepExecution] Setting up subscription for session:', sessionId);
    subscriptionRef.current = graphqlSubscribe(
      onSessionEntry,
      { sessionId },
      (data) => {
        const entry = data?.onSessionEntry;
        if (!entry || entry.entryType !== STEP_RESULT_ENTRY) return;

        console.log('[StepExecution] Received STEP_RESULT via subscription:', {
          sortKey: entry.sortKey,
          createdAt: entry.createdAt,
        });

        // Clear any active polling since subscription delivered the result
        if (pollTimerRef.current) {
          console.log('[StepExecution] Clearing fallback poll — subscription delivered result');
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          pollAttemptRef.current = 0;
        }

        // Parse step result data
        const parsed = safeParse(entry.data);

        // Match result to current step and determine pass/fail
        setStepStatuses((prev) => {
          const executingEntry = Object.entries(prev).find(
            ([, status]) => status === 'EXECUTING'
          );
          if (!executingEntry) {
            console.warn('[StepExecution] Received STEP_RESULT but no step is EXECUTING');
            return prev;
          }

          const idx = Number(executingEntry[0]);
          const newStatus = isStepFailure(parsed) ? 'FAILED' : 'COMPLETED';
          console.log(`[StepExecution] Step ${idx + 1} result via subscription: ${newStatus}`);
          setStepResults((r) => ({ ...r, [idx]: parsed }));
          return { ...prev, [idx]: newStatus };
        });
      },
      (err) => {
        console.warn('[StepExecution] Subscription error:', err?.message || err);
      }
    );

    // Mark subscription as ready after a brief setup period
    setSubscriptionReady(true);
    console.log('[StepExecution] Subscription established');

    return () => {
      try {
        subscriptionRef.current?.unsubscribe();
      } catch {
        /* no-op */
      }
      subscriptionRef.current = null;
      setSubscriptionReady(false);
    };
  }, [sessionId]);

  // --- Fallback polling: checks DB for STEP_RESULT when subscription may have missed it ---
  const startPollingForResult = useCallback((executingIndex) => {
    // Don't start if already polling
    if (pollTimerRef.current) return;

    pollAttemptRef.current = 0;
    console.log(`[StepExecution] Starting fallback poll for step ${executingIndex + 1} result`);

    pollTimerRef.current = setInterval(async () => {
      pollAttemptRef.current += 1;

      try {
        const res = await graphqlQuery(getSessionTimeline, {
          sessionId,
          entryType: STEP_RESULT_ENTRY,
        });

        const items = (res?.getSessionTimeline?.items || [])
          .filter((e) => e?.entryType === STEP_RESULT_ENTRY)
          .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

        // Check if we have a result for the executing step index
        // Step results are ordered by creation time; the number of results
        // tells us which step index they correspond to.
        if (items.length > executingIndex) {
          const resultEntry = items[executingIndex];
          const parsed = safeParse(resultEntry.data);
          const newStatus = isStepFailure(parsed) ? 'FAILED' : 'COMPLETED';

          console.log(`[StepExecution] Poll found result for step ${executingIndex + 1}: ${newStatus}`);

          setStepStatuses((prev) => {
            if (prev[executingIndex] === 'EXECUTING') {
              setStepResults((r) => ({ ...r, [executingIndex]: parsed }));
              return { ...prev, [executingIndex]: newStatus };
            }
            return prev;
          });

          // Stop polling
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          pollAttemptRef.current = 0;
        }
      } catch (err) {
        console.warn('[StepExecution] Poll query failed:', err?.message || err);
      }

      // Stop polling after max attempts
      if (pollAttemptRef.current >= POLL_MAX_ATTEMPTS) {
        console.warn(`[StepExecution] Max poll attempts reached for step ${executingIndex + 1}, stopping poll`);
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        pollAttemptRef.current = 0;
      }
    }, POLL_INTERVAL_MS);
  }, [sessionId]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  // Auto-advance to next step when current step completes or fails
  useEffect(() => {
    const currentStatus = stepStatuses[currentStepIndex];
    if (currentStatus === 'COMPLETED' || currentStatus === 'DENIED' || currentStatus === 'FAILED') {
      console.log(`[StepExecution] Step ${currentStepIndex + 1} finished with status: ${currentStatus}, advancing in ${STEP_ADVANCE_DELAY_MS}ms`);
      // Small delay before showing next step for visual feedback
      const timer = setTimeout(() => {
        if (currentStepIndex < steps.length - 1 && !agentStopped) {
          setCurrentStepIndex((prev) => prev + 1);
        }
      }, STEP_ADVANCE_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [stepStatuses, currentStepIndex, steps.length, agentStopped]);

  const handleApprove = useCallback(async (index) => {
    if (submitting || agentStopped) return;
    setSubmitting(true);
    setError('');

    console.log(`[StepExecution] Approving step ${index + 1}/${steps.length}`, {
      stepId: steps[index]?.step_id,
      title: steps[index]?.title,
      subscriptionReady,
    });

    try {
      const step = steps[index];
      await graphqlMutation(postApproval, {
        input: {
          sessionId,
          decision: 'ALLOW',
          reason: `Step ${index + 1}: ${step.step_id || step.title || ''}`,
        },
      });

      console.log(`[StepExecution] Step ${index + 1} approval sent successfully, marking EXECUTING`);
      setStepStatuses((prev) => ({ ...prev, [index]: 'EXECUTING' }));
      setConsecutiveDenials(0);

      // Start fallback polling in case the subscription misses the result
      startPollingForResult(index);
    } catch (err) {
      console.error('[StepExecution] Step approval failed:', err);
      setError(err.message || 'Failed to approve step.');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, steps, submitting, agentStopped, subscriptionReady, startPollingForResult]);

  const handleDeny = useCallback(async (index) => {
    if (submitting || agentStopped) return;
    setSubmitting(true);
    setError('');

    console.log(`[StepExecution] Denying step ${index + 1}/${steps.length}`, {
      stepId: steps[index]?.step_id,
      title: steps[index]?.title,
    });

    try {
      const step = steps[index];
      await graphqlMutation(postApproval, {
        input: {
          sessionId,
          decision: 'DISCARD',
          reason: `Step ${index + 1}: ${step.step_id || step.title || ''}`,
        },
      });

      setStepStatuses((prev) => ({ ...prev, [index]: 'DENIED' }));

      const newConsecutive = consecutiveDenials + 1;
      setConsecutiveDenials(newConsecutive);
      console.log(`[StepExecution] Step ${index + 1} denied. Consecutive denials: ${newConsecutive}`);

      if (newConsecutive >= MAX_CONSECUTIVE_DENIALS) {
        console.log('[StepExecution] Max consecutive denials reached — stopping agent');
        setAgentStopped(true);
      }
    } catch (err) {
      console.error('[StepExecution] Step denial failed:', err);
      setError(err.message || 'Failed to deny step.');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, steps, submitting, agentStopped, consecutiveDenials]);

  if (!steps || steps.length === 0) {
    return (
      <div className="alert alert-warning">No steps available for execution.</div>
    );
  }

  const lastStatus = stepStatuses[steps.length - 1];
  const allStepsProcessed = currentStepIndex >= steps.length - 1 &&
    (lastStatus === 'COMPLETED' || lastStatus === 'DENIED' || lastStatus === 'FAILED');

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Step-by-Step Execution</h5>
        <span className="badge bg-primary">
          Step {Math.min(currentStepIndex + 1, steps.length)} of {steps.length}
        </span>
      </div>
      <div className="card-body">
        {allStepsProcessed && !agentStopped && (
          <div className="alert alert-success mb-3" role="alert">
            <strong>Execution Complete.</strong> All steps have been processed.
          </div>
        )}

        {/* Step history */}
        {steps.map((step, index) => {
          const status = stepStatuses[index];
          const isCurrent = index === currentStepIndex && !agentStopped && !allStepsProcessed;
          const isVisible = index <= currentStepIndex;

          if (!isVisible) return null;

          return (
            <div
              key={step.step_id || index}
              className={`card mb-3 ${isCurrent ? 'border-primary' : ''}`}
            >
              <div className="card-body py-3">
                {/* Step header */}
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2 flex-wrap">
                  <h6 className="mb-0 d-flex align-items-center gap-2">
                    <span className="badge bg-secondary rounded-pill">{index + 1}</span>
                    <span>{step.title || step.step_id || `Step ${index + 1}`}</span>
                  </h6>
                  <div className="d-flex align-items-center gap-2">
                    {status === 'APPROVED' && (
                      <span className="badge bg-success">Approved</span>
                    )}
                    {status === 'EXECUTING' && (
                      <span className="badge bg-info">
                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                        Executing...
                      </span>
                    )}
                    {status === 'COMPLETED' && (
                      <span className="badge bg-success">&#10003; Completed</span>
                    )}
                    {status === 'DENIED' && (
                      <span className="badge bg-danger">&#10007; Denied</span>
                    )}
                    {status === 'FAILED' && (
                      <span className="badge bg-danger">Failed</span>
                    )}
                  </div>
                </div>

                {/* Step description */}
                {step.description && (
                  <p className="text-muted mb-2">{step.description}</p>
                )}

                {/* Step command */}
                {step.command && (
                  <pre
                    className="bg-dark text-light p-2 rounded small mb-2"
                    style={{ overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    <code>{step.command}</code>
                  </pre>
                )}

                {/* Expected output */}
                {step.expected_output && (
                  <p className="mb-2 small">
                    <strong>Expected output:</strong> {step.expected_output}
                  </p>
                )}

                {/* Step result (after execution) — shows pass/fail status */}
                {(status === 'COMPLETED' || status === 'FAILED') && (
                  <div className={`mt-2 p-3 rounded small border ${status === 'COMPLETED' ? 'border-success bg-success bg-opacity-10' : 'border-danger bg-danger bg-opacity-10'}`}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      {status === 'COMPLETED' ? (
                        <span className="badge bg-success">&#10003; Step Passed</span>
                      ) : (
                        <span className="badge bg-danger">&#10007; Step Failed</span>
                      )}
                      <span className="text-muted">
                        {status === 'COMPLETED'
                          ? 'This step executed successfully.'
                          : 'This step encountered an error during execution.'}
                      </span>
                    </div>
                    {stepResults[index] && (
                      <pre
                        className="bg-dark text-light p-2 rounded small mb-0 mt-1"
                        style={{ overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        <code>
                          {typeof stepResults[index] === 'string'
                            ? stepResults[index]
                            : JSON.stringify(stepResults[index], null, 2)}
                        </code>
                      </pre>
                    )}
                  </div>
                )}

                {/* Action buttons — only for current step */}
                {isCurrent && !status && (
                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-success d-flex align-items-center gap-1"
                      onClick={() => handleApprove(index)}
                      disabled={submitting}
                    >
                      <span style={{ fontSize: '1.2em' }}>&#10003;</span>
                      {submitting ? 'Submitting...' : 'Approve Execution'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger d-flex align-items-center gap-1"
                      onClick={() => handleDeny(index)}
                      disabled={submitting}
                    >
                      <span style={{ fontSize: '1.2em' }}>&#10007;</span>
                      {submitting ? 'Submitting...' : 'Deny Execution'}
                    </button>
                  </div>
                )}

                {/* Waiting for result indicator */}
                {status === 'EXECUTING' && (
                  <div className="mt-2 text-muted small">
                    Waiting for execution result...
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Agent stopped message — shown below the latest step */}
        {agentStopped && (
          <div className="card border-warning mb-3">
            <div className="card-body py-3 d-flex align-items-start gap-2">
              <span style={{ fontSize: '1.3em' }}>&#9888;</span>
              <div>
                <strong>Execution paused.</strong>
                <p className="mb-0 mt-1 small text-muted">
                  Two consecutive steps were denied. The agent has gracefully stopped execution.
                  No further steps will be processed for this session.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default StepExecution;
